import { can } from "../../../../auth/can.ts";
import { isBranchConstrained } from "../../../../auth/sessionAccess.ts";
import React, { useEffect, useState } from "react";

import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import XLSX from "xlsx-js-style";
import * as moment from "moment-timezone";

import { useAppSettings } from "../../../../hooks/useAppSettings.tsx";
import Title from "../../../Title.tsx";
import Layout from "../../../Layout.tsx";
import { RootState } from "../../../../store/index.ts";
import {
  calcularTiempoTrabajado,
  calcularTurno,
  ReporteRelojChecadorDefaultValues,
} from "../../../../interfaces/formik/IReporteRelojChecador.ts";
import ReporteRelojChecadorForm from "../../../../forms/ReporteRelojChecadorForm.tsx";
import { readBranches } from "../../../../services/branch.service.ts";
import Branch from "../../../../models/Branch.ts";
import ReporteRelojChecadorSchema from "../../../../validation/schemas/ReporteRelojChecadorSchema.ts";
import { findCodeChecksForReport } from "../../../../services/assistanceCheck.service.ts";
import {
  readClockHoursByBranch,
  readHoursByBranch,
} from "../../../../services/branchSchedule.service.ts";
import ModalContainer from "../../../ModalContainer.tsx";
import { setShow } from "../../../../store/slices/reportClockSlice.ts";
import ModalCheckDetails from "./LogsCheckModal.tsx";
import { toCamelCase } from "../../../../hooks/toCameCase.tsx";
import TableReporteChequeoCodigo from "./TableReporteChequeoCodigo.tsx";

const ChequeoCodigo = () => {
  useAppSettings();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { show } = useSelector((state: RootState) => state.reportClock);
  const { user } = useSelector((state: RootState) => state.auth);
  const [branchs, setBranchs] = useState([]);
  const [title, setTitle] = useState("Reporte por códigos");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: ReporteRelojChecadorDefaultValues(),
    validationSchema: ReporteRelojChecadorSchema,
    onSubmit: async (values) => {
      setRecords([]);
      setLoading(true);
      try {
        const { branch, partner, start, end } = values;
        if (formik.values.branch !== -99) {
          const selectedBranch = branchs.find(
            (b: Branch) => Number(b.id) === Number(formik.values.branch)
          ) as any;
          if (selectedBranch?.name)
            setTitle(`Reporte por códigos ${selectedBranch?.name}`);
        }

        let queryStart = start;
        const queryEnd = moment.utc(end).add(1, "day").format("YYYY-MM-DD");
        const branchNum = Number(branch);
        if (Number.isFinite(branchNum) && branchNum > 0 && branchNum !== -99) {
          try {
            const hours = can("attendance.view", user)
              ? await readHoursByBranch(branchNum)
              : can("attendance.check", user)
                ? await readClockHoursByBranch(branchNum)
                : null;
            if (Boolean(hours?.nightScheduleEnabled)) {
              queryStart = moment
                .utc(start)
                .subtract(1, "day")
                .format("YYYY-MM-DD");
            }
          } catch {
            queryStart = start;
          }
        }

        const data = await findCodeChecksForReport(
          branch,
          partner,
          queryStart,
          queryEnd
        );
        setRecords(data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchDependencies = async () => {
    const number =
      isBranchConstrained(user) ? Number(user.branchId) : Number(id);
    try {
      const branches = await readBranches();
      const selected = branches.find((b: Branch) => Number(b.id) === number);
      setTitle(`Reporte por códigos - ${selected?.name}`);
      await formik.setFieldValue("branch", selected?.id);
      setBranchs(branches);
    } catch (error) {
      console.log(error);
    } finally {
    }
    setLoading(false);
  };

  const handleDownload = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);
    const head = [
      "Fechas consultadas:",
      `${moment(formik.values.start).format("MMMM D [del] YYYY")}`,
      `${moment(formik.values.end).format("MMMM D [del] YYYY")}`,
    ];
    XLSX.utils.sheet_add_aoa(ws, [head], { origin: "A1" });
    const userGenerated = ["Generado por:", `${user?.name} ${user?.lastName}`];
    XLSX.utils.sheet_add_aoa(ws, [userGenerated], { origin: "A2" });

    const headers = [
      "Chilito",
      "Puesto",
      "Turno",
      "Fecha",
      "Entrada",
      "Salida",
      "Horas trabajadas",
      "Sucursal",
    ];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: "A3" });
    let rowIndex = 4;

    records.forEach((rec: any) => {
      const row = [
        rec.person_firstName,
        rec.position_name,
        calcularTurno(rec.assistanceCheck_checkTime ?? ""),
        moment.utc(rec.assistanceCheck_checkDate).format("DD-MM-YYYY"),
        rec.assistanceCheck_checkTime,
        "",
        "",
        rec.branch_name,
      ];
      XLSX.utils.sheet_add_aoa(ws, [row], { origin: `A${rowIndex}` });
      rowIndex += 1;
    });

    ws["!cols"] = [
      { wch: 25 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 15 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Registros");
    XLSX.writeFile(
      wb,
      `Chequeos-Codigos - ${moment(formik.values.start).format(
        "DD-MM-YYYY"
      )} - ${moment(formik.values.end).format("DD-MM-YYYY")}.xlsx`
    );
  };

  useEffect(() => {
    if (user) {
      fetchDependencies();
    }
  }, [user]);

  useEffect(() => {
    formik.handleSubmit();
  }, [branchs, id]);

  return (
    <React.Fragment>
      <Layout loading={loading}>
        <Title
          baseTitle="Sucursales"
          basePath="/sucursales"
          activeTitle="Reporte por códigos"
          title={title}
        />

        <div
          className="card border-0 m-3 m-md-4 shadow-sm overflow-hidden"
          style={{ borderRadius: "16px" }}
        >
          <div
            style={{
              background: "#212529",
              color: "#f8fafc",
              padding: "12px clamp(14px, 2.5vw, 20px)",
              fontSize: "0.95rem",
              fontWeight: 600,
              letterSpacing: "0.02em",
            }}
          >
            Formulario para el reporte
          </div>
          <div
            className="w-100"
            style={{
              padding: "clamp(12px, 2.5vw, 20px)",
              boxSizing: "border-box",
            }}
          >
            <ReporteRelojChecadorForm
              formik={formik}
              branches={branchs}
              disabled={isBranchConstrained(user)}
              handleDownload={() => handleDownload()}
            />
          </div>
        </div>

        <div className="card border-0 m-4 rounded">
          <div className="tab-content p-3">
            <TableReporteChequeoCodigo
              records={records}
              loading={loading}
              start={formik.values.start}
              end={formik.values.end}
            />
          </div>
        </div>
      </Layout>
    </React.Fragment>
  );
};

export default ChequeoCodigo;
